import { Alert } from "@itandi/itandi-bb-ui/Alert";
import { Button } from "@itandi/itandi-bb-ui/Button";
import type React from "react";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { SurveyModal } from "~/components/common/survey/SurveyModal";
import { useDealSurveys } from "~/hooks/deals/useDealSurveys";
import useItandiAccount from "~/hooks/useItandiAccount";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { Deal } from "~/models/Deal";
import { canMovingStatus } from "~/models/Staff";
import { FINISHED } from "~/models/Status";
import type { SurveyAnswer } from "~/models/Survey";
import Styles from "./StatusHandleButtons.module.scss";
import { StatusReasonModal } from "./StatusReasonModal";

type Props = {
	alertText: string | null;
	canBackward: boolean;
	deal: Deal;
	forwardDisabled: boolean;
	handleBackwardStatus: (reason: string) => Promise<void>;
	handleForwardStatus: () => Promise<void>;
	handleForwardStatusWithSurvey: (
		surveyAnswers: SurveyAnswer[]
	) => Promise<void>;
	handlePartiallyFinish?: (() => Promise<void>) | null;
	nextStatusId: number;
};

export const StatusHandleReasonButtons: React.FC<Props> = ({
	canBackward,
	handleForwardStatus,
	handleForwardStatusWithSurvey,
	handleBackwardStatus,
	forwardDisabled,
	alertText,
	nextStatusId,
	deal,
	handlePartiallyFinish = null
}: Props) => {
	const { data: account } = useItandiAccount().currentAccount();
	const {
		isOpen: openConfirm,
		handleOpen: handleOpenConfirm,
		handleClose: handleCloseConfirm
	} = useOpenHandler();
	const {
		isOpen: openBackConfirm,
		handleOpen: handleOpenBackConfirm,
		handleClose: handleCloseBackConfirm
	} = useOpenHandler();
	const {
		isOpen: openPartiallyFinishConfirm,
		handleOpen: handleOpenPartiallyFinishConfirm,
		handleClose: handleClosePartiallyFinishConfirm
	} = useOpenHandler();
	const {
		isOpen: openSurveyConfirm,
		handleOpen: handleOpenSurveyConfirm,
		handleClose: handleCloseSurveyConfirm
	} = useOpenHandler();
	const handleAcceptForward = async (): Promise<void> => {
		await handleForwardStatus();
		handleCloseConfirm();
	};
	const handleAcceptBackward = async (reason: string): Promise<void> => {
		await handleBackwardStatus(reason);
		handleCloseBackConfirm();
	};
	const handleAcceptForwardWithSurvey = async (
		surveyAnswers: SurveyAnswer[]
	): Promise<void> => {
		await handleForwardStatusWithSurvey(surveyAnswers);
		handleCloseSurveyConfirm();
	};
	const { data: surveys } = useDealSurveys().index(deal.id);
	const surveyPresent =
		!account?.isAdmin &&
		nextStatusId === FINISHED &&
		surveys !== null &&
		surveys.length !== 0;
	const handleAcceptPartiallyFinish = async (): Promise<void> => {
		if (handlePartiallyFinish !== null) {
			await handlePartiallyFinish();
		}
	};
	if (account === null) return null;
	return (
		<div className={Styles.StatusHandleButtonRow}>
			{handlePartiallyFinish !== null && (
				<Button
					disabled={!canMovingStatus(account)}
					height="default"
					onClick={handleOpenPartiallyFinishConfirm}
					variant="tertiary"
				>
					工事を依頼せずに途中完了する
				</Button>
			)}
			<div className={Styles.RightButtons}>
				{canBackward ? (
					<button
						className={Styles.BackwardButton}
						disabled={!canMovingStatus(account)}
						onClick={handleOpenBackConfirm}
						type="button"
					>
						ステータスを戻す
					</button>
				) : null}
				<button
					className={Styles.ForwardButton}
					disabled={forwardDisabled || !canMovingStatus(account)}
					onClick={surveyPresent ? handleOpenSurveyConfirm : handleOpenConfirm}
					type="button"
				>
					ステータスを進める
				</button>
			</div>
			<ConfirmModal
				actionText="確定"
				confirmType="save"
				handleAccept={handleAcceptPartiallyFinish}
				handleCancel={handleClosePartiallyFinishConfirm}
				isOpen={openPartiallyFinishConfirm}
			>
				<p className={Styles.ConfirmText}>
					案件を完了扱いにしてよろしいですか？
				</p>
				<Alert variant="warning">
					施工会社には工事が依頼されていない状態で完了扱いになります。
				</Alert>
			</ConfirmModal>
			<ConfirmModal
				actionText="確定"
				confirmType="save"
				handleAccept={handleAcceptForward}
				handleCancel={handleCloseConfirm}
				isOpen={openConfirm}
			>
				<p className={Styles.ConfirmText}>ステータスを進めてよろしいですか？</p>
				{alertText ? <Alert variant="info">{alertText}</Alert> : null}
			</ConfirmModal>
			{openBackConfirm ? (
				<StatusReasonModal
					actionText="ステータスを戻す"
					confirmText="ステータスを戻してよろしいですか？"
					handleAccept={handleAcceptBackward}
					handleClose={handleCloseBackConfirm}
					label="ステータスを戻す理由（施工会社に通知されます)"
					title="ステータスを戻す"
				/>
			) : null}
			{surveyPresent ? (
				<SurveyModal
					handleAccept={handleAcceptForwardWithSurvey}
					handleClose={handleCloseSurveyConfirm}
					isOpen={openSurveyConfirm}
					surveys={surveys}
				/>
			) : null}
		</div>
	);
};
